import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type { CommunityProject, CommunityProjectResponse, TrainerCard } from '../lib/types';
import { Button, EmptyState, ErrorState, LoadingGrid, Panel } from '../components/ui';
import { CommunityProjectPanel } from '../components/CommunityProjectPanel';
import { PlayerCard } from '../components/profile/PlayerCard';
import { formatDate, formatNumber } from '../lib/format';

interface DiscoverResponse {
  activeNow: TrainerCard[];
  newTrainers: TrainerCard[];
  topHelpers: TrainerCard[];
  shinyHunters: TrainerCard[];
}

export function CommunityPage() {
  const navigate = useNavigate();
  const { push } = useToast();
  const { me } = useSession();
  const state = useAsync<DiscoverResponse>(() => api.get('/community/discover'));
  // The shared goal, loaded alongside the people. Two reads rather than one
  // because they change on completely different schedules — the project moves
  // every few seconds on a busy server, the trainer lists barely at all.
  const projects = useAsync<CommunityProjectResponse>(() => api.get('/community-projects'));
  const [project, setProject] = useState<CommunityProject | null>(null);

  useEffect(() => {
    if (projects.data) setProject(projects.data.project);
  }, [projects.data]);

  async function visitRandom() {
    try {
      const result = await api.get<{ username: string | null }>('/community/random');
      if (result.username) navigate(`/trainer/${result.username}`);
      else push('info', 'No new trainers to visit right now — check back later!');
    } catch {
      push('error', 'Could not find anyone right now.');
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const helpedToday = me?.helpedToday ?? 0;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Community</h1>
          <div className="page-head__sub">
            Feed other trainers' Pokémon to help them grow — and earn Friendship Tokens doing it.
          </div>
        </div>
        <Button variant="go" size="lg" onClick={() => void visitRandom()}>
          ♥ Visit someone new
        </Button>
      </div>

      {/* The shared goal comes first: it is the one thing on this page that
          everybody is doing together, and burying it under trainer lists would
          make it the thing nobody notices. */}
      <CommunityProjectPanel
        project={project}
        season={projects.data?.season}
        onChange={setProject}
      />

      {/* A tally, not a meter. There is no ceiling to draw a bar against. */}
      <Panel title="◈ Helping out" raised={false}>
        <div className="stack-sm">
          <div className="row row--between tiny mono">
            <span>
              {helpedToday === 0
                ? "You haven't helped anyone yet today"
                : `${helpedToday} Pokémon and eggs helped today`}
            </span>
          </div>
          <p className="tiny muted">
            Feed as many Pokémon as you like — each one just needs ten minutes before you can feed
            it again. Reaching a trainer you haven't helped today is worth a little extra.
          </p>
        </div>
      </Panel>

      {state.loading ? (
        <LoadingGrid count={8} height={130} />
      ) : (
        <div className="stack">
          <TrainerRow
            title="◐ Active right now"
            hint="Most likely to help you back"
            trainers={state.data?.activeNow ?? []}
            empty="Nobody else has been online in the last day."
          />
          <TrainerRow
            title="◔ New trainers"
            hint="Give someone a warm welcome"
            trainers={state.data?.newTrainers ?? []}
            empty="No new trainers yet."
          />
          <TrainerRow
            title="♥ Top helpers"
            hint="These trainers feed the most Pokémon"
            trainers={state.data?.topHelpers ?? []}
            empty="Nobody has fed anyone yet — be the first!"
          />
          <TrainerRow
            title="✦ Shiny collectors"
            hint="Worth a look"
            trainers={state.data?.shinyHunters ?? []}
            empty="No shinies have been hatched yet."
          />

          <Panel title="♛ Leaderboards" action={<Link className="btn btn--sm" to="/leaderboard">View all</Link>}>
            <p className="tiny muted">
              Rankings for the Pokédex, hatching, helping, shinies and trainer level.
            </p>
          </Panel>
        </div>
      )}
    </div>
  );
}

function TrainerRow({
  title,
  hint,
  trainers,
  empty,
}: {
  title: string;
  hint: string;
  trainers: TrainerCard[];
  empty: string;
}) {
  return (
    <Panel title={title} hint={hint}>
      {trainers.length === 0 ? (
        <EmptyState art="◌" title={empty} />
      ) : (
        <div className="pcard-grid">
          {trainers.map((trainer) => (
            <PlayerCard key={trainer.userId} trainer={trainer} showHelped />
          ))}
        </div>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------------- */

interface LeaderboardRow {
  rank: number;
  username: string;
  trainerName: string;
  value: number;
  /** Dex board only: when this trainer reached the count shown. */
  reachedAt?: string | null;
  complete?: boolean;
}

interface LeaderboardResponse {
  topHelpers: LeaderboardRow[];
  topHatchers: LeaderboardRow[];
  topShinies: LeaderboardRow[];
  topTrainers: LeaderboardRow[];
  topDex: LeaderboardRow[];
  speciesTotal: number;
}

export function LeaderboardPage() {
  const state = useAsync<LeaderboardResponse>(() => api.get('/community/leaderboard'));

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const speciesTotal = state.data?.speciesTotal ?? 0;

  const boards: Array<{ title: string; hint?: string; rows: LeaderboardRow[]; total?: number }> = [
    {
      // First, because it is the one board a place on is meant to be permanent.
      title: '◉ Pokédex',
      hint: 'Ties go to whoever got there first',
      rows: state.data?.topDex ?? [],
      total: speciesTotal,
    },
    { title: '♥ Most helpful', rows: state.data?.topHelpers ?? [] },
    { title: '◓ Most hatched', rows: state.data?.topHatchers ?? [] },
    { title: '✦ Most shinies', rows: state.data?.topShinies ?? [] },
    { title: '★ Highest level', rows: state.data?.topTrainers ?? [] },
  ];

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Leaderboards</h1>
          <div className="page-head__sub">Updated live as trainers play.</div>
        </div>
        <Link className="btn" to="/community">← Community</Link>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {boards.map((board) => (
          <Panel key={board.title} title={board.title} hint={board.hint}>
            {board.rows.length === 0 ? (
              <EmptyState art="◌" title="No entries yet" />
            ) : (
              <div className="stack-sm">
                {board.rows.map((row) => (
                  <div key={row.username} className="row row--between">
                    <span className="row" style={{ gap: 8 }}>
                      <span
                        className="mono tiny"
                        style={{
                          width: 22,
                          textAlign: 'right',
                          color: row.rank <= 3 ? 'var(--gold-dark)' : 'var(--ink-faint)',
                        }}
                      >
                        {row.rank}
                      </span>
                      <span>
                        <Link to={`/trainer/${row.username}`} className="tiny">
                          {row.trainerName}
                        </Link>
                        {row.complete && row.reachedAt && (
                          <span className="tiny muted" style={{ display: 'block' }}>
                            Completed {formatDate(row.reachedAt)}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="mono tiny">
                      {formatNumber(row.value)}
                      {board.total ? ` / ${formatNumber(board.total)}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        ))}
      </div>
    </div>
  );
}
